<template>
  <div class="speech-translation-page">
    <!-- 顶部：标题 + 操作按钮 -->
    <div class="page-header">
      <h2 class="page-title">{{ t('speechTranslation.pageTitle') }}</h2>
      <div class="header-actions">
        <template v-if="!sessionStarted">
          <a-button
            type="primary"
            size="middle"
            :loading="creating"
            :disabled="!sourceLanguage || targetLanguages.length === 0"
            @click="handleStart"
          >
            {{ t('speechTranslation.startMeeting') }}
          </a-button>
        </template>
        <template v-else>
          <a-alert
            v-if="speechError"
            :message="speechError"
            type="error"
            show-icon
            closable
            class="header-error"
          />
          <a-space align="center" :size="12">
            <a-button
              v-if="!isRecognizing"
              type="primary"
              size="middle"
              :loading="startingMic"
              @click="handleStartMic"
            >
              {{ t('speechTranslation.resume') }}
            </a-button>
            <a-button v-else type="primary" danger size="middle" @click="handleStopMic">
              {{ t('speechTranslation.pause') }}
            </a-button>
            <a-button size="middle" @click="handleEnd">{{ t('speechTranslation.end') }}</a-button>
          </a-space>
        </template>
      </div>
    </div>

    <!-- 主体：左 66.7%（选择语言+输出面板），右 33.3%（字幕设置+二维码复制链接） -->
    <div class="page-body">
      <div class="main-grid">
        <div class="left-panel">
          <a-card class="content-card" size="small">
            <template #title>
              <span>{{ t('speechTranslation.selectLanguage') }}</span>
              <a-button
                v-if="sessionStarted && languageChanged"
                type="primary"
                size="small"
                :loading="confirmingLanguage"
                class="lang-confirm-btn"
                @click="handleConfirmLanguage"
              >
                {{ t('speechTranslation.confirm') }}
              </a-button>
            </template>
            <LanguageSelector
              :all-languages="allLanguages"
              :source-language="sourceLanguage"
              :target-languages="targetLanguages"
              @update:source-language="sourceLanguage = $event"
              @update:target-languages="targetLanguages = $event"
            />
          </a-card>
          <a-card class="content-card output-panel" :title="t('speechTranslation.outputPanel')" size="small">
            <div
              class="output-content output-content-cols"
              ref="outputContentRef"
              :style="outputGridStyle"
            >
              <template v-if="outputColumns.length">
                <div
                  v-for="col in outputColumns"
                  :key="col.code"
                  class="output-col"
                >
                  <div class="output-col-header">{{ col.label }}</div>
                  <div class="output-col-body">
                    <!-- 实时内容始终显示在最上方，其次是最新的历史内容（displayFragments 已按时间倒序） -->
                    <template v-if="currentRecognizing">
                      <div class="output-line output-realtime">
                        {{ col.code === '__source__' ? (lastSource || t('speechTranslation.realtimePlaceholder')) : (lastTranslations[col.code] ?? '') }}
                      </div>
                    </template>
                    <div
                      v-for="(line, idx) in col.lines"
                      :key="idx"
                      class="output-line"
                    >
                      {{ line }}
                    </div>
                  </div>
                </div>
              </template>
              <div v-else-if="!currentRecognizing" class="output-empty">
                {{ t('speechTranslation.outputEmpty') }}
              </div>
            </div>
          </a-card>
        </div>
        <div class="right-panel">
          <a-card class="content-card" :title="t('speechTranslation.subtitleStyle')" size="small">
            <SubtitleSettings ref="subtitleSettingsRef" :compact="true" />
            <div class="subtitle-actions">
              <a-button size="small" @click="handleSubtitleRestoreDefault">{{ t('speechTranslation.restoreDefault') }}</a-button>
              <a-button
                type="primary"
                size="small"
                :loading="savingSubtitle"
                @click="handleSubtitleConfirm"
              >
                {{ t('speechTranslation.confirm') }}
              </a-button>
            </div>
          </a-card>
          <a-card class="content-card qrcode-card" :title="t('speechTranslation.share')" size="small">
            <QRCodeShare :url="fullShareLink" :compact="true" />
          </a-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, unref } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import { useUserStore } from '@/stores'

const { t } = useI18n()
import {
  createSession,
  endSession,
  getShareLink,
  heartbeat,
  getSubtitlePreset,
  putSubtitlePreset,
  saveSnapshot,
  updateSessionLanguages
} from '../api'
import { getRecognitionLangs, type LangItem, type LanguageGroup } from '../api/language'
import { MAX_DISPLAY_LINES } from '../constants'
import { formatLangDisplayLabel } from '../utils/lang-display-label'
import { useSpeechTranslation } from '../composables/useSpeechTranslation'
import LanguageSelector from '../components/LanguageSelector.vue'
import SubtitleSettings from '../components/SubtitleSettings.vue'
import QRCodeShare from '../components/QRCodeShare.vue'
import type { ShareLinkResponse, SubtitleStyle, SubtitlePresetResponse } from '../api/session'

const userStore = useUserStore()
const token = computed(() => userStore.token || '')

const allLanguages = ref<LangItem[]>([])
const sourceLanguage = ref<LangItem | null>(null)
const targetLanguages = ref<LangItem[]>([])

const outputContentRef = ref<HTMLElement | null>(null)

const creating = ref(false)
const sessionStarted = ref(false)
const appId = ref('')
const shareLink = ref('')
const fullShareLink = computed(() => {
  if (!shareLink.value) return ''
  const path = shareLink.value.startsWith('/') ? shareLink.value : `/${shareLink.value}`
  return `${window.location.origin}${path}`
})
const sourceLangCode = ref('')
const targetLangCodes = ref<string[]>([])
/** 与 targetLangCodes 一一对应的 Azure 目标语言代码（接口 shortCode/azureTargetCode） */
const targetLangAzureCodes = ref<string[]>([])
const startingMic = ref(false)
const confirmingLanguage = ref(false)
const micStream = ref<MediaStream | null>(null)
const speechInstance = ref<ReturnType<typeof useSpeechTranslation> | null>(null)
const subtitleSettingsRef = ref<InstanceType<typeof SubtitleSettings> | null>(null)
const savingSubtitle = ref(false)
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
/** 累计识别时长（秒），仅在心跳运行时累加，暂停不计入 */
const elapsedSeconds = ref(0)

const isRecognizing = computed(() => unref(speechInstance.value?.isRecognizing) ?? false)
const speechError = computed(() => unref(speechInstance.value?.error) ?? null)
const lastSource = computed(() => unref(speechInstance.value?.lastSource) ?? '')
const lastTranslations = computed(() => unref(speechInstance.value?.lastTranslations) ?? {})
const fragments = computed(() => unref(speechInstance.value?.fragments) ?? [])

const currentRecognizing = computed(() => isRecognizing.value && (!!lastSource.value || Object.keys(lastTranslations.value).length > 0))

/** 会议中且当前选中的语言与已应用的不一致时显示「确定」 */
const languageChanged = computed(() => {
  if (!sessionStarted.value || !sourceLanguage.value) return false
  if (sourceLanguage.value.languageCode !== sourceLangCode.value) return true
  const selected = targetLanguages.value.map((l) => l.languageCode).sort()
  const applied = [...targetLangCodes.value].sort()
  if (selected.length !== applied.length) return true
  return selected.some((c, i) => c !== applied[i])
})

  /** 只保留最近 N 条，超出从顶部顶出；最新在最前（顶部） */
const displayFragments = computed(() => {
  const list = fragments.value.slice(-MAX_DISPLAY_LINES)
  return list.slice().reverse()
})

  /** 按列展示：源语言列 + 各目标语言列，每列含标题与行数组；未开始会议时为空以显示占位 */
const outputColumns = computed(() => {
  if (!sessionStarted.value) return []
  const sourceCode = sourceLangCode.value
  const targets = targetLangCodes.value
  const list = displayFragments.value
  const cols: Array<{ code: string; label: string; lines: string[] }> = []
  cols.push({
    code: '__source__',
    label: getDisplayLabelForCode(sourceCode),
    lines: list.map((f) => f.sourceText)
  })
  targets.forEach((code) => {
    cols.push({
      code,
      label: getDisplayLabelForCode(code),
      lines: list.map((f) => f.translations[code] ?? '')
    })
  })
  return cols
})

const outputGridStyle = computed(() => ({
  gridTemplateColumns: outputColumns.value.length
    ? `repeat(${outputColumns.value.length}, minmax(0, 1fr))`
    : '1fr'
}))

/** 输出面板标题：中文显示中文名，其他语言显示 ISO 639-1 短码大写 */
function getDisplayLabelForCode(code: string): string {
  const lang = allLanguages.value.find((l) => l.languageCode === code)
  return formatLangDisplayLabel(lang ?? { languageCode: code })
}

watch(
  () => displayFragments.value.length + lastSource.value,
  () => {
    nextTick(() => {
      const el = outputContentRef.value
      if (el) el.scrollTop = 0
    })
  }
)

async function loadLanguages() {
  if (!token.value) return
  try {
    const groups = await getRecognitionLangs(token.value)
    if (groups?.length) {
      const flat = groups.flatMap((g: LanguageGroup) => g.languageList ?? g.languages ?? [])
      allLanguages.value = flat.filter((x): x is LangItem => x != null && typeof x.languageCode === 'string')
    }
  } catch (error) {
    console.error('loadLanguages failed:', error)
  }
}

/** 进入页面即拉取用户固定观看链接，用于分享卡片二维码与复制链接 */
async function loadShareLink() {
  if (!token.value) return
  try {
    const res = (await getShareLink()) as unknown as ShareLinkResponse | undefined
    if (res?.shareLink) shareLink.value = res.shareLink
  } catch {
    // 无权限或未登录时静默失败，分享卡片无链接
  }
}

async function handleStart() {
  if (!sourceLanguage.value || targetLanguages.value.length === 0) {
    message.warning(t('speechTranslation.selectSourceAndTarget'))
    return
  }

  const source = {
    languageCode: sourceLanguage.value.languageCode,
    languageName: sourceLanguage.value.languageName,
    languageEnName: sourceLanguage.value.languageEnName
  }
  const targets = targetLanguages.value.map((l) => ({
    languageCode: l.languageCode,
    languageName: l.languageName,
    languageEnName: l.languageEnName
  }))

  creating.value = true
  try {
    const res = (await createSession({ sourceLanguage: source, targetLanguages: targets })) as unknown as { appId: string; shareLink: string }
    appId.value = res.appId
    shareLink.value = res.shareLink
    sourceLangCode.value = sourceLanguage.value!.languageCode
    targetLangCodes.value = targetLanguages.value.map((l) => l.languageCode)
    targetLangAzureCodes.value = targetLanguages.value.map(
      (l) => l.azureTargetCode ?? l.shortCode ?? l.languageCode
    )
    sessionStarted.value = true
    elapsedSeconds.value = 0
    message.success(t('speechTranslation.meetingCreated'))
    // 打开新页签：大白板翻译展示页（标题「翻译中」）
    const viewerUrl = `${window.location.origin}${shareLink.value.startsWith('/') ? shareLink.value : `/${shareLink.value}`}`
    window.open(viewerUrl, '_blank', 'noopener,noreferrer')
    // 会议创建后自动开启麦克风识别，显示「暂停」和「结束」按钮
    await handleStartMic()
  } catch (error) {
    // 创建失败的具体原因（包括用量不足）统一由全局 API 错误处理器弹出。
    // 这里仅在开发环境打日志，避免出现「用量不足」+「创建会议失败」双重提示。
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('createSession failed:', error)
    }
  } finally {
    creating.value = false
  }
}

async function handleStartMic() {
  if (!appId.value || !sourceLangCode.value || !targetLangCodes.value.length) return
  startingMic.value = true
  try {
    micStream.value = await navigator.mediaDevices.getUserMedia({ audio: true })
    const instance = useSpeechTranslation({
      appId: appId.value,
      sourceLanguage: sourceLangCode.value,
      targetLanguages: targetLangCodes.value,
      targetLanguageAzureCodes: targetLangAzureCodes.value,
      audioStream: micStream.value
    })
    speechInstance.value = instance
    await instance.start()
    // start() 内部若 token/SDK 失败会设置 error 且不抛错，需在此判断并释放麦克风
    if (instance.error.value && !instance.isRecognizing.value) {
      const errMsg =
        instance.error.value || t('speechTranslation.speechTokenError')
      message.error(errMsg)
      if (micStream.value) {
        micStream.value.getTracks().forEach((t) => t.stop())
        micStream.value = null
      }
      speechInstance.value = null
      return
    }
    heartbeatTimer = setInterval(() => {
      if (appId.value) {
        heartbeat(appId.value, 10)
        elapsedSeconds.value += 10
      }
    }, 10000)
  } catch (e: unknown) {
    console.error('handleStartMic failed:', e)
    if (micStream.value) {
      micStream.value.getTracks().forEach((t) => t.stop())
      micStream.value = null
    }
  } finally {
    startingMic.value = false
  }
}

async function handleStopMic() {
  if (speechInstance.value) {
    await speechInstance.value.stop()
    speechInstance.value = null
  }
  if (micStream.value) {
    micStream.value.getTracks().forEach((t) => t.stop())
    micStream.value = null
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

async function handleEnd() {
  if (isRecognizing.value) {
    await handleStopMic()
  }
  if (!appId.value) return
  try {
    await endSession(appId.value, elapsedSeconds.value)
    message.success(t('speechTranslation.meetingEnded'))
    sessionStarted.value = false
    appId.value = ''
    elapsedSeconds.value = 0
  } catch (error) {
    console.error('endSession failed:', error)
  }
}

/** 会议中换语言：先 snapshot，再更新会话语言，停止识别器后按新语言重连 */
async function handleConfirmLanguage() {
  if (!appId.value || !sourceLanguage.value || !targetLanguages.value.length) return
  confirmingLanguage.value = true
  try {
    try {
      await saveSnapshot(appId.value)
    } catch (error) {
      console.error('saveSnapshot failed:', error)
    }
    const source = {
      languageCode: sourceLanguage.value.languageCode,
      languageName: sourceLanguage.value.languageName,
      languageEnName: sourceLanguage.value.languageEnName
    }
    const targets = targetLanguages.value.map((l) => ({
      languageCode: l.languageCode,
      languageName: l.languageName,
      languageEnName: l.languageEnName
    }))
    await updateSessionLanguages(appId.value, { sourceLanguage: source, targetLanguages: targets })
    await handleStopMic()
    sourceLangCode.value = sourceLanguage.value.languageCode
    targetLangCodes.value = targetLanguages.value.map((l) => l.languageCode)
    targetLangAzureCodes.value = targetLanguages.value.map(
      (l) => l.azureTargetCode ?? l.shortCode ?? l.languageCode
    )
    await handleStartMic()
    message.success(t('speechTranslation.languageSwitched'))
  } catch (error) {
    console.error('handleConfirmLanguage failed:', error)
  } finally {
    confirmingLanguage.value = false
  }
}

async function loadSubtitlePreset() {
  try {
    const style = await getSubtitlePreset()
    await nextTick()
    subtitleSettingsRef.value?.setStyle(style)
  } catch (error) {
    console.error('loadSubtitlePreset failed:', error)
  }
}

function showSubtitleSaveFeedback(res: SubtitlePresetResponse) {
  if (res.liveSyncFailed) {
    message.warning(t('speechTranslation.subtitlePresetLiveSyncFailed'))
  } else if (res.liveSynced) {
    message.success(t('speechTranslation.subtitlePresetSavedAndSynced'))
  } else {
    message.success(t('speechTranslation.subtitlePresetSaved'))
  }
}

async function handleSubtitleConfirm() {
  if (!subtitleSettingsRef.value) return
  const style = subtitleSettingsRef.value.getStyle() as SubtitleStyle
  savingSubtitle.value = true
  try {
    const res = (await putSubtitlePreset(style)) as SubtitlePresetResponse
    showSubtitleSaveFeedback(res)
  } catch (error) {
    console.error('putSubtitlePreset failed:', error)
    message.error(t('speechTranslation.updateSubtitleFailed'))
  } finally {
    savingSubtitle.value = false
  }
}

/** 恢复默认：表单重置为系统 DEFAULT 并落库（有会则 live sync） */
async function handleSubtitleRestoreDefault() {
  if (!subtitleSettingsRef.value) return
  const defaultStyle = subtitleSettingsRef.value.getDefaultStyle()
  subtitleSettingsRef.value.reset()
  savingSubtitle.value = true
  try {
    const res = (await putSubtitlePreset(defaultStyle)) as SubtitlePresetResponse
    if (res.liveSyncFailed) {
      message.warning(t('speechTranslation.subtitlePresetLiveSyncFailed'))
    } else if (res.liveSynced) {
      message.success(t('speechTranslation.restoreDefaultSuccess'))
    } else {
      message.success(t('speechTranslation.restoreDefaultSaved'))
    }
  } catch (error) {
    console.error('restore default subtitle style failed:', error)
    message.error(t('speechTranslation.restoreDefaultFailed'))
  } finally {
    savingSubtitle.value = false
  }
}

onMounted(() => {
  loadLanguages()
  loadShareLink()
  loadSubtitlePreset()
})
</script>

<style scoped lang="less">
/* 不在本页内嵌滚动，交给 MainLayout .page-content 统一纵向滚动 */
.speech-translation-page {
  display: flex;
  flex-direction: column;
  padding: 6px 16px 12px;
  box-sizing: border-box;
  overflow: visible;
}

.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
  min-height: 36px;

  .page-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    :deep(.ant-btn) {
      height: 28px;
      padding: 0 12px;
    }
  }

  .header-error {
    max-width: 240px;
    margin-bottom: 0;
  }
}

.page-body {
  flex: 0 1 auto;
  min-height: 0;
  overflow: visible;
}

.main-grid {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;

  .content-card {
    overflow: visible;
    display: flex;
    flex-direction: column;

    :deep(.ant-card-head) {
      .lang-confirm-btn {
        margin-left: auto;
      }
    }
    :deep(.ant-card-head-title) {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
    }

    :deep(.ant-card-body) {
      flex: 0 1 auto;
      min-height: 0;
      overflow: visible;
    }
  }
}

.left-panel {
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;

  .content-card {
    flex-shrink: 0;
  }

  .content-card.output-panel {
    flex: 0 1 auto;
    min-height: 200px;
  }
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;

  .content-card {
    flex-shrink: 0;
  }

  /* 字幕样式随页面整体滚动，不再单独出现滚动条 */
  .content-card:first-child {
    flex: 0 1 auto;
  }

  .subtitle-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .qrcode-card :deep(.ant-card-body) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
}

.output-panel {
  min-width: 0;

  .output-content {
    flex: 0 1 auto;
    min-height: 120px;
    overflow: visible;
    padding: 8px 0;
    font-size: 13px;
  }

  .output-content-cols {
    display: grid;
    gap: 0 12px;
    align-items: start;
  }

  .output-col {
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
  }

  .output-col-header {
    font-weight: 600;
    color: #1890ff;
    margin-bottom: 6px;
    flex-shrink: 0;
    font-size: 12px;
  }

  .output-col-body {
    min-height: 0;
  }

  .output-line {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.85);
    line-height: 1.5;
    margin-bottom: 4px;
    word-break: break-word;

    &.output-realtime {
      color: #52c41a;
      font-weight: 500;
    }
  }

  .output-empty {
    color: rgba(0, 0, 0, 0.3);
    text-align: center;
    padding: 24px;
  }
}

@media (max-width: 1366px) {
  .speech-translation-page {
    padding: 10px 12px;
  }

  .page-header {
    margin-bottom: 8px;
    gap: 10px;

    .page-title {
      font-size: 16px;
    }
  }

  .main-grid {
    gap: 8px;
  }

  .right-panel .subtitle-actions {
    margin-top: 8px;
    gap: 6px;
  }
}
</style>
